// services/astro-bot-miniapp/src/screens/onboarding/AddingToWorld.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api-client.js';
import { useAuth } from '../../store/auth.js';
import { CosmosBackdrop } from '../../components/brand/CosmosBackdrop.js';
import { OrbitalRings } from '../../components/brand/OrbitalRings.js';
import { LivingSigil } from '../../components/brand/LivingSigil.js';
import { ProgressCheckpoints, type CheckpointItem, type CheckpointState } from '../../components/onboarding/ProgressCheckpoints.js';
import styles from './AddingToWorld.module.css';

interface Resp { selfie: { status: 'absent' | 'pending' | 'ready' | 'failed' } }

const ADVANCE_AFTER_POLLS = [1, 6, 12] as const;

function stateAt(index: number, polls: number, total: number): CheckpointState {
  const threshold = ADVANCE_AFTER_POLLS[index];
  const nextThreshold = ADVANCE_AFTER_POLLS[index + 1] ?? Infinity;
  if (index === total - 1 && polls >= threshold) return 'active';
  if (polls >= nextThreshold) return 'done';
  if (polls >= threshold) return 'active';
  return 'pending';
}

export function AddingToWorld(): JSX.Element {
  const { t } = useTranslation('flows');
  const jwt = useAuth(s => s.jwt);
  const nav = useNavigate();
  const [pollCount, setPollCount] = useState(0);
  const [error, setError] = useState<'none' | 'soft' | 'fatal'>('none');
  const failCount = useRef(0);

  useEffect(() => {
    if (!jwt) return;
    let cancelled = false;

    const tick = async (): Promise<void> => {
      try {
        const r = await apiFetch('/api/v1/star-persona', jwt);
        if (!r.ok) throw new Error(`star_persona_${r.status}`);
        const j = (await r.json()) as Resp;
        if (cancelled) return;
        failCount.current = 0;
        setError('none');
        setPollCount(c => c + 1);
        if (j.selfie.status === 'absent') { nav('/onboarding/pick', { replace: true }); return; }
        if (j.selfie.status === 'ready')  { nav('/onboarding/personal', { replace: true }); return; }
        if (j.selfie.status === 'failed') { nav('/onboarding/personal', { replace: true }); return; }
      } catch (err) {
        console.error('[onboarding-poll]', err);
        failCount.current += 1;
        if (failCount.current >= 6) setError('fatal');
        else if (failCount.current >= 3) setError('soft');
      }
    };

    void tick();
    const poll = setInterval(tick, 1500);
    return () => { cancelled = true; clearInterval(poll); };
  }, [jwt, nav]);

  const archetype = t('onboarding.reveal.archetype.placeholderName');

  const checkpoints = useMemo<CheckpointItem[]>(() => {
    const labels = [
      t('onboarding.addingToWorld.checkpoints.reading'),
      t('onboarding.addingToWorld.checkpoints.mapping', { archetype }),
      t('onboarding.addingToWorld.checkpoints.painting'),
    ];
    return labels.map((label, i) => ({
      id: ['reading', 'mapping', 'painting'][i],
      label,
      state: stateAt(i, pollCount, labels.length),
    }));
  }, [pollCount, t, archetype]);

  return (
    <div className={styles.screen}>
      <CosmosBackdrop />
      <div className={styles.orbitContainer}>
        <OrbitalRings />
        <div className={styles.sigilWrapper}><LivingSigil size={160} state="breathing" /></div>
      </div>
      <div className={styles.textOverlay}>
        <h2 className={styles.title}>{t('onboarding.addingToWorld.title', { archetype })}</h2>
        <ProgressCheckpoints
          items={checkpoints}
          ariaLabel={t('onboarding.addingToWorld.checkpointsAria', { defaultValue: 'Generation progress' })}
        />
        <p className={styles.eta}>{t('onboarding.addingToWorld.eta')}</p>
        {error === 'soft'  && <p className={styles.errSoft}>{t('onboarding.addingToWorld.errorRetry')}</p>}
        {error === 'fatal' && (
          <button type="button" className={styles.errFatal} onClick={() => nav('/', { replace: true })}>
            {t('onboarding.addingToWorld.errorFatal')}
          </button>
        )}
      </div>
    </div>
  );
}
